// Slice 2C5A · GuidedDocumentFamilyPanel — the guided-intake panel for a
// single family. Slice 2C5A only mounts this for `family === "proposal"`.
//
// Contract:
//   - Owns question answers + wording-source lifecycle (generated / customized
//     / legacy / later) FOR THIS FAMILY only.
//   - Renders the live JobClose AI recommended-wording preview.
//   - Renders the four action buttons (Use recommended · Edit wording ·
//     Help me change it · Set up later).
//   - When the contractor chooses Edit wording, calls `onSwitchToEditor`
//     with the pre-filled bag and the guided panel steps back — the parent's
//     existing paragraph editor (DocumentDefaultsFamilyCard raw fields)
//     takes over.
//   - Blocks Use recommended when required Company Profile data is missing
//     (e.g. company_name) and offers a route to Business & Trades.
//   - Runs the Slice 2C4 shared placeholder validator on the resolved bag
//     before Use recommended issues its PUT.
//
// The panel never issues writes directly — it hands the resolved payload to
// `onSaveGuided({ answers, action, wording_source, resolved_fields })` which
// the parent forwards to `PUT /api/document-guided-setup/{family}`.

import { useMemo, useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/context/LangContext";
import {
  generateProposalWording, findFieldsWithUnresolvedPlaceholders, applyHelpMeChangeIt,
} from "@/lib/documentPresets";
import {
  FAMILY_QUESTION_SCHEMAS, emptyAnswersForFamily, getFamilyQuestions,
  setAnswer, toggleNote, answersDifferMeaningfully, buildCompareEntries,
  guidedDraftKey, readGuidedDraft, writeGuidedDraft, clearGuidedDraft,
  isGuidedDirty, guidedBaselineFromRecord,
} from "@/lib/documentGuidedSetup";

const FAMILY_ALLOWLIST = {
  proposal: [
    "proposal_intro", "proposal_footer",
    "deposit_terms", "final_payment_terms", "late_payment_note",
  ],
};

const isRequiredMissingCompany = (missing) =>
  Array.isArray(missing) && missing.some((k) => k === "companyName");

const ProposalGuidedPanel = ({
  family,                 // "proposal"
  userId,
  companyProfile,
  guidedRecord,
  savedWording,
  onSaveGuided,
  onSetLater,
  onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  const { t, lang } = useLang();
  const questions = getFamilyQuestions("proposal", lang);
  const allowlist = FAMILY_ALLOWLIST.proposal;

  // ---- Answers + adjustments state, hydrated from localStorage first,
  // then falling back to the persisted companion record.
  const baseline = guidedBaselineFromRecord(guidedRecord);
  const [answers, setAnswers] = useState(() => {
    const persisted = readGuidedDraft(userId, "proposal");
    if (persisted && persisted.answers) return persisted.answers;
    return { ...emptyAnswersForFamily("proposal"), ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const persisted = readGuidedDraft(userId, "proposal");
    return (persisted && persisted.adjustments) || {};
  });
  const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
  const wordingSource = (guidedRecord && guidedRecord.wording_source) || null;

  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  // Slice 2C5A correction · Multi-tab / newer-baseline conflict state.
  const [conflict, setConflict] = useState(null);  // { newerUpdatedAt, newerAnswers, newerSource } | null

  // ---- Guided-draft persistence: mirror answers + adjustments to
  // localStorage on every change so a refresh restores the exact panel
  // state. Never issues a server write.
  useEffect(() => {
    if (!userId) return;
    const dirty = isGuidedDirty({ answers, adjustments }, baseline);
    if (dirty) {
      writeGuidedDraft(userId, "proposal", {
        answers, adjustments,
        baseline_updated_at: baseline.updated_at || null,
      });
    } else {
      clearGuidedDraft(userId, "proposal");
    }
  }, [userId, answers, adjustments, baseline.updated_at, baseline.answers, baseline.wording_source]);

  // ---- Multi-tab: listen for storage events on the guided-draft key
  // and on the guided-record baseline key. Both surface the same
  // conflict panel (Load latest · Keep editing · Overwrite with my changes).
  useEffect(() => {
    if (!userId) return;
    const draftKey = guidedDraftKey(userId, "proposal");
    const onStorage = (e) => {
      if (e.key !== draftKey) return;
      // Another tab wrote a newer guided draft. Only surface a conflict
      // when the current tab is ALSO dirty (i.e. has unsaved edits).
      if (!isGuidedDirty({ answers, adjustments }, baseline)) return;
      let payload = null;
      try { payload = e.newValue ? JSON.parse(e.newValue) : null; } catch (_e) { payload = null; }
      setConflict({
        source: "other_tab_draft",
        newerAnswers: (payload && payload.answers) || {},
        newerAdjustments: (payload && payload.adjustments) || {},
        newerBaselineUpdatedAt: (payload && payload.baseline_updated_at) || null,
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId, answers, adjustments, baseline.updated_at]);

  // ---- Newer server baseline (either fresh mount or after guidedRecord
  // prop changes) that disagrees with the current local baseline_updated_at
  // triggers the conflict panel as well.
  useEffect(() => {
    const persisted = readGuidedDraft(userId, "proposal");
    if (!persisted) return;
    if (!baseline.updated_at) return;
    if (persisted.baseline_updated_at && persisted.baseline_updated_at !== baseline.updated_at) {
      setConflict({
        source: "newer_saved_baseline",
        serverAnswers: baseline.answers,
        serverSource: baseline.wording_source,
        serverUpdatedAt: baseline.updated_at,
      });
    }
  }, [userId, baseline.updated_at, baseline.answers, baseline.wording_source]);

  // Explicit dirty pill (Unsaved changes) mirrors Slice 2C2B semantics.
  const dirty = isGuidedDirty({ answers, adjustments }, baseline);

  const discardDraft = () => {
    setAnswers({ ...emptyAnswersForFamily("proposal"), ...baseline.answers });
    setAdjustments({});
    clearGuidedDraft(userId, "proposal");
    setConflict(null);
  };
  const loadLatest = () => {
    // Load the newer server baseline; drop local edits.
    setAnswers({ ...emptyAnswersForFamily("proposal"), ...baseline.answers });
    setAdjustments({});
    clearGuidedDraft(userId, "proposal");
    setConflict(null);
  };
  const keepEditing = () => setConflict(null);
  const overwriteWithMine = () => {
    // Keep local answers + adjustments; bump the baseline pointer so we
    // stop showing the conflict panel. No server write here — the
    // explicit Use-recommended button is still required.
    writeGuidedDraft(userId, "proposal", {
      answers, adjustments,
      baseline_updated_at: baseline.updated_at || null,
    });
    setConflict(null);
  };

  // Recompute the recommended wording every time answers change.
  const gen = useMemo(() => {
    const base = generateProposalWording({
      companyName: (companyProfile || {}).company_name,
      phone:       (companyProfile || {}).phone,
      email:       (companyProfile || {}).email,
      answers,
      lang,
    });
    if (!base.ok || !base.wording) return base;
    return { ...base, wording: applyHelpMeChangeIt(base.wording, adjustments) };
  }, [answers, adjustments, companyProfile, lang]);

  const badKeys = useMemo(
    () => (gen.ok ? findFieldsWithUnresolvedPlaceholders(gen.wording) : []),
    [gen],
  );
  const canUseRecommended = gen.ok && badKeys.length === 0;

  // ---- Answer-change reconciliation for customized wording -----------------
  const isCustomized = wordingSource === "customized";
  const answersChangedSinceCustomization =
    isCustomized && answersDifferMeaningfully(answers, savedAnswers);
  useEffect(() => {
    if (answersChangedSinceCustomization && !reconcileOpen && !compareOpen) {
      setReconcileOpen(true);
    }
  }, [answersChangedSinceCustomization, reconcileOpen, compareOpen]);

  // Slice 2C5A boundary — this panel is authorized ONLY for the Proposal
  // family. Enforce after hooks so React's hook-order invariant holds.
  if (family !== "proposal") return null;

  // ---- Answer setters ------------------------------------------------------
  const onChip     = (key, v) => setAnswers((a) => setAnswer(a, key, v));
  const onNumber   = (key, v) => setAnswers((a) => setAnswer(a, key, v === "" ? undefined : Number(v)));
  const onText     = (key, v) => setAnswers((a) => setAnswer(a, key, v));
  const onCheck    = (key)    => setAnswers((a) => toggleNote(a, key));

  // ---- Actions -------------------------------------------------------------
  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError("");
    setBusy(true);
    try {
      // Slice 2C5A · Browser-QA fix (Message-follow-up 4).
      //   The exact previewed recommendation is `gen.wording`, which
      //   already reflects the questionnaire answers (including any
      //   Help-me-change-it Tone override — Help now writes to
      //   `answers.tone` directly, not a parallel bag) PLUS any
      //   `adjustments.length` refinement baked into resolved_fields
      //   via `applyHelpMeChangeIt`. Saving `answers` + `resolved_fields`
      //   makes that exact recommendation the persisted baseline: on
      //   refresh, `generateProposalWording({answers})` reproduces the
      //   same intro tone, and `document_defaults` retains the exact
      //   wording that was displayed.
      await onSaveGuided({
        answers,
        action: "use_recommended",
        wording_source: "generated",
        resolved_fields: gen.wording,
      });
      // Successful save → drop the guided localStorage draft and reset
      // the local session-only refinement bag. Dirty goes false,
      // Discard-draft affordance disappears, UNSAVED CHANGES clears,
      // Ready status was already set by the server (Slice 2C5A strict-
      // ordered save). No status write happens here.
      setAdjustments({});
      clearGuidedDraft(userId, "proposal");
      setReconcileOpen(false);
      setCompareOpen(false);
    } catch (e) {
      // API failure contract: DO NOT clear the guided draft, DO NOT
      // change `adjustments`, DO NOT surface a success toast, DO NOT
      // touch READY / Later status. The current preview, Discard-draft
      // affordance, and UNSAVED CHANGES pill all remain intact so the
      // contractor can retry or discard explicitly.
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };
  const doSetLater = async () => {
    setError("");
    setBusy(true);
    try { await onSetLater(); } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const doEditWording = () => {
    if (!gen.ok || !gen.wording) return;
    onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" });
  };
  const doKeepCustomized = () => {
    setReconcileOpen(false);
    setCompareOpen(false);
    // Reset answers to whatever was saved with the customized wording so
    // the panel no longer shows a pending-change banner.
    setAnswers({ ...emptyAnswersForFamily("proposal"), ...savedAnswers });
  };

  // ---- Legacy branch -------------------------------------------------------
  const hasAnyLegacyWording =
    !guidedRecord &&
    savedWording &&
    allowlist.some((k) => (savedWording[k] || "").trim().length > 0);

  if (hasAnyLegacyWording && !helpOpen && !reconcileOpen && !compareOpen) {
    return (
      <div className="space-y-3" data-testid="guided-proposal-legacy">
        <div className="text-sm text-muted-foreground">
          Current wording
        </div>
        <div className="rounded-md border p-3 space-y-2">
          {allowlist.map((k) => {
            const v = (savedWording && savedWording[k]) || "";
            if (!v) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, " ")}</div>
                {v}
              </div>
            );
          })}
        </div>
        <Button
          type="button" variant="outline" size="sm"
          data-testid="guided-proposal-answer-questions"
          onClick={() => setHelpOpen(true)}
        >
          Answer a few short questions to use JobClose AI recommended wording
        </Button>
      </div>
    );
  }

  // ---- Missing required Company Profile banner ----------------------------
  const requiredCompanyMissing = !gen.ok && isRequiredMissingCompany(gen.missing);

  return (
    <div className="space-y-4" data-testid="guided-proposal-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground" data-testid="guided-proposal-intro">
          Answer a few short questions. JobClose AI will write the professional wording for you.
        </div>
        {dirty && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900"
            data-testid="guided-proposal-unsaved-pill"
          >
            Unsaved changes
          </span>
        )}
      </div>

      {conflict && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
          data-testid="guided-proposal-conflict"
        >
          <div className="text-sm font-semibold">
            {conflict.source === "other_tab_draft"
              ? t("guided.conflict_newer_other_tab")
              : t("guided.conflict_newer_saved")}
          </div>
          <div className="text-sm">{t("guided.conflict_choose_reconcile")}</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={loadLatest}
              data-testid="guided-proposal-conflict-load-latest">{t("guided.conflict_load_latest")}</Button>
            <Button size="sm" variant="outline" onClick={keepEditing}
              data-testid="guided-proposal-conflict-keep-editing">{t("guided.conflict_keep_editing")}</Button>
            <Button size="sm" variant="ghost" onClick={overwriteWithMine}
              data-testid="guided-proposal-conflict-overwrite">{t("guided.conflict_overwrite")}</Button>
          </div>
        </div>
      )}

      {requiredCompanyMissing && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="guided-proposal-missing-profile">
          Complete your Business &amp; Trades company name before JobClose AI can generate proposal wording.
          <div className="mt-2">
            <Button
              type="button" variant="secondary" size="sm"
              data-testid="guided-proposal-goto-business"
              onClick={onNavigateToBusinessAndTrades}
            >
              Go to Business &amp; Trades
            </Button>
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-4" data-testid="guided-proposal-questions">
        {questions.map((q) => (
          <QuestionRow
            key={q.id} q={q} answers={answers}
            onChip={onChip} onNumber={onNumber} onText={onText} onCheck={onCheck}
          />
        ))}
      </div>

      {/* Recommended-wording preview */}
      {gen.ok && (
        <div className="rounded-md border p-3 space-y-2" data-testid="guided-proposal-preview">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">{t("guided.recommended_wording")}</div>
            {wordingSource === "generated" && (
              <Badge variant="secondary" data-testid="guided-proposal-refreshed-badge">
                Preview refreshed from your latest answers
              </Badge>
            )}
          </div>
          {allowlist.map((k) => {
            const v = gen.wording[k];
            if (v === "" || v == null) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap" data-testid={`guided-proposal-preview-${k}`}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, " ")}</div>
                {v}
              </div>
            );
          })}
        </div>
      )}

      {/* Help-me-change-it panel */}
      {helpOpen && (
        <HelpMeChangeItPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          warrantySeparate={!!answers.notes_warranty_separate}
          onWarrantySeparateChange={(v) =>
            setAnswers((a) => setAnswer(a, "notes_warranty_separate", v))
          }
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((prev) => ({ ...prev, length: l }))}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2" data-testid="guided-proposal-actions">
        <Button
          type="button" size="sm"
          disabled={!canUseRecommended || busy}
          data-testid="guided-proposal-use-recommended"
          onClick={doUseRecommended}
        >
          Use JobClose AI recommended wording
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          disabled={!gen.ok || busy}
          data-testid="guided-proposal-edit-wording"
          onClick={doEditWording}
        >
          Edit wording
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          data-testid="guided-proposal-help"
          aria-expanded={helpOpen}
          onClick={() => setHelpOpen((v) => !v)}
        >
          Help me change it
          {helpOpen
            ? <ChevronUp   className="ml-1 h-3 w-3" aria-hidden="true" />
            : <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />}
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          data-testid="guided-proposal-set-later"
          disabled={busy}
          onClick={doSetLater}
        >
          Set up later
        </Button>
        {dirty && (
          <Button
            type="button" size="sm" variant="outline"
            data-testid="guided-proposal-discard-draft"
            onClick={discardDraft}
          >
            Discard draft
          </Button>
        )}
      </div>

      {error && (
        <div className="text-sm text-destructive" data-testid="guided-proposal-error">{error}</div>
      )}

      {/* Answer-change reconciliation (customized → answers changed) */}
      {reconcileOpen && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
             data-testid="guided-proposal-reconcile">
          <div className="text-sm font-semibold">Your answers changed after you saved customized wording.</div>
          <div className="text-sm">Choose how to reconcile the update:</div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button" size="sm"
              data-testid="guided-proposal-reconcile-use-updated"
              disabled={!canUseRecommended || busy}
              onClick={doUseRecommended}
            >
              Use updated recommended wording
            </Button>
            <Button
              type="button" size="sm" variant="outline"
              data-testid="guided-proposal-reconcile-keep"
              onClick={doKeepCustomized}
            >
              Keep my customized wording
            </Button>
            <Button
              type="button" size="sm" variant="ghost"
              data-testid="guided-proposal-reconcile-compare"
              onClick={() => { setCompareOpen(true); setReconcileOpen(false); }}
            >
              Compare changes
            </Button>
          </div>
        </div>
      )}

      {compareOpen && (
        <CompareChangesView
          entries={buildCompareEntries(savedWording, gen.wording, allowlist)}
          onClose={() => setCompareOpen(false)}
          onUseUpdated={doUseRecommended}
          onKeep={doKeepCustomized}
          canUseRecommended={canUseRecommended}
        />
      )}
    </div>
  );
};

const QuestionRow = ({ q, answers, onChip, onNumber, onText, onCheck }) => {
  if (q.kind === "chip") {
    const current = answers[q.key] ?? q.defaultValue;
    return (
      <div className="space-y-2" data-testid={`guided-q-${q.id}`}>
        <div className="text-sm font-medium">{q.prompt}</div>
        <div className="flex flex-wrap gap-2">
          {q.choices.map((c) => (
            <Button
              key={c.value}
              type="button" size="sm"
              variant={current === c.value ? "default" : "outline"}
              data-testid={`guided-q-${q.id}-${c.value}`}
              onClick={() => onChip(q.key, c.value)}
            >
              {c.label}
            </Button>
          ))}
        </div>
        {(q.conditionalInputs || []).map((ci) =>
          current === ci.when ? (
            <div key={ci.key} className="flex items-center gap-2">
              <div className="text-xs text-muted-foreground w-40">{ci.label}</div>
              <Input
                type={ci.kind === "number" ? "number" : "text"}
                value={answers[ci.key] ?? ""}
                min={ci.min} max={ci.max}
                placeholder={ci.placeholder || ""}
                data-testid={`guided-q-${q.id}-${ci.key}`}
                onChange={(e) => (ci.kind === "number"
                  ? onNumber(ci.key, e.target.value)
                  : onText(ci.key, e.target.value))}
              />
            </div>
          ) : null
        )}
        {q.conditionalChip && q.conditionalChip.appearWhen(current) && (
          <div className="pl-2 space-y-2">
            <div className="text-xs font-medium">{q.conditionalChip.prompt}</div>
            <div className="flex flex-wrap gap-2">
              {q.conditionalChip.choices.map((c) => {
                const active = (answers[q.conditionalChip.key] ?? q.conditionalChip.defaultValue) === c.value;
                return (
                  <Button
                    key={c.value} type="button" size="sm"
                    variant={active ? "default" : "outline"}
                    data-testid={`guided-q-${q.conditionalChip.key}-${c.value}`}
                    onClick={() => onChip(q.conditionalChip.key, c.value)}
                  >
                    {c.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
  if (q.kind === "checkbox_group") {
    return (
      <div className="space-y-2" data-testid={`guided-q-${q.id}`}>
        <div className="text-sm font-medium">{q.prompt}</div>
        <div className="space-y-1">
          {q.choices.map((c) => (
            <label key={c.value} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={!!answers[c.value]}
                data-testid={`guided-q-${q.id}-${c.value}`}
                onCheckedChange={() => onCheck(c.value)}
              />
              {c.label}
            </label>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const HelpMeChangeItPanel = ({
  tone, onToneChange,
  warrantySeparate, onWarrantySeparateChange,
  length, onLengthChange,
  onClose,
}) => {
  const { t } = useLang();
  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="guided-proposal-help-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="guided-proposal-help-close">{t("guided.close")}</Button>
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.tone")}</div>
        <div className="flex flex-wrap gap-2">
          {/* Slice 2C5A · Browser-QA fix (Message-follow-up 4).
              Help-me-change-it Tone writes into the SAME `answers.tone`
              key the main questionnaire uses (values: friendly /
              professional / straightforward). This unifies the two
              tone controls, guarantees the exact previewed
              recommendation is what actually persists on Use-recommended
              save, and eliminates the label inconsistency where the
              helper displayed a mismatched label while the questionnaire displayed
              "Straightforward". */}
          {[
            ["friendly",        t("guided.tone_friendly")],
            ["professional",    t("guided.tone_professional")],
            ["straightforward", t("guided.tone_straightforward")],
          ].map(([value, label]) => (
            <Button
              key={value} size="sm"
              variant={tone === value ? "default" : "outline"}
              data-testid={`guided-proposal-help-tone-${value}`}
              onClick={() => onToneChange(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.length")}</div>
        <div className="flex flex-wrap gap-2">
          {[
            ["shorter",       t("guided.length_shorter")],
            ["standard",      t("guided.length_standard")],
            ["more_detailed", t("guided.length_more_detailed")],
          ].map(([v, label]) => (
            <Button
              key={v} size="sm"
              variant={length === v ? "default" : "outline"}
              data-testid={`guided-proposal-help-length-${v}`}
              onClick={() => onLengthChange(v)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_clauses")}</div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm"
            variant={warrantySeparate ? "default" : "outline"}
            data-testid="guided-proposal-help-clause-add"
            onClick={() => onWarrantySeparateChange(true)}>
            Add warranty-separately clause
          </Button>
          <Button size="sm"
            variant={!warrantySeparate ? "default" : "outline"}
            data-testid="guided-proposal-help-clause-remove"
            onClick={() => onWarrantySeparateChange(false)}>
            Remove warranty-separately clause
          </Button>
        </div>
      </div>
    </div>
  );
};

const CompareChangesView = ({ entries, onClose, onUseUpdated, onKeep, canUseRecommended }) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-proposal-compare">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.compare_changes")}</div>
      <Button size="sm" variant="ghost" onClick={onClose} data-testid="guided-proposal-compare-close">{t("guided.close")}</Button>
    </div>
    {entries.length === 0 && (
      <div className="text-sm text-muted-foreground" data-testid="guided-proposal-compare-empty">
        No wording changes were produced by the new answers.
      </div>
    )}
    {entries.map(({ key, customized, generated }) => (
      <div key={key} className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t pt-2" data-testid={`guided-proposal-compare-${key}`}>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">My customized · {key.replace(/_/g, " ")}</div>
          <div className="text-sm whitespace-pre-wrap" data-testid={`guided-proposal-compare-${key}-customized`}>{customized || <span className="text-muted-foreground">(empty)</span>}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Updated recommendation · {key.replace(/_/g, " ")}</div>
          <div className="text-sm whitespace-pre-wrap" data-testid={`guided-proposal-compare-${key}-generated`}>{generated || <span className="text-muted-foreground">(empty)</span>}</div>
        </div>
      </div>
    ))}
    <div className="flex flex-wrap gap-2 pt-2">
      <Button size="sm"
        data-testid="guided-proposal-compare-use-updated"
        disabled={!canUseRecommended}
        onClick={onUseUpdated}>
        Use updated recommended wording
      </Button>
      <Button size="sm" variant="outline"
        data-testid="guided-proposal-compare-keep"
        onClick={onKeep}>
        Keep my customized wording
      </Button>
    </div>
  </div>
  );
};

// ---------------------------------------------------------------------------
// Slice 2C5B · GuidedContractPanel — compact family-specific panel for
// Contract. Reuses the shared guided-save endpoint, guided-draft store,
// and family-expansion mechanism. No new save system, no new draft
// system, no new expansion system.
//
// Renders:
//   - Six practical Contract questions (2 chip-groups + 2 multi-selects
//     + 2 more chip groups, per FAMILY_QUESTION_SCHEMAS.contract).
//   - Live Contract-wording preview via `generateContractWording`.
//   - Five action buttons: Use recommended · Edit wording ·
//     Help me change it · Handle outside JobClose AI · Set up later.
//   - Optional collapsed Help-me-change-it refinement panel (Length +
//     Optional emphasis). Tone lives in `answers.tone`.
//
// Uses the exact save-path Proposal locked in Slice 2C5A: strict-ordered
// defaults → companion → status LAST → audit on the server.
// ---------------------------------------------------------------------------
import {
  generateContractWording, applyHelpMeChangeItContract,
  generateInvoiceWording, applyHelpMeChangeItInvoice,
  generateCustomerUpdateWording, applyHelpMeChangeItCustomerUpdate,
  generateCloseoutWording, applyHelpMeChangeItCloseout,
  generateWarrantyWording, applyHelpMeChangeItWarranty,
} from "@/lib/documentPresets";
import {
  toggleMultiSelect,
  addTradeOverride, updateTradeOverride, removeTradeOverride,
  WARRANTY_PERIOD_VALUES, WARRANTY_PERIOD_LABELS,
} from "@/lib/documentGuidedSetup";

const CONTRACT_ALLOWLIST = [
  "contract_terms", "contractor_disclaimer", "customer_approval_language",
];

const ContractHelpPanel = ({
  tone, onToneChange,
  length, onLengthChange,
  emphasize, onToggleEmphasize,
  onClose,
}) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-contract-help-panel">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
      <Button size="sm" variant="ghost" onClick={onClose} data-testid="guided-contract-help-close">Close</Button>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.tone")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["friendly", t("guided.tone_friendly")],
          ["professional", t("guided.tone_professional")],
          ["straightforward", t("guided.tone_straightforward")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={tone === v ? "default" : "outline"}
            data-testid={`guided-contract-help-tone-${v}`}
            onClick={() => onToneChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.length")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["shorter", t("guided.length_shorter")],
          ["standard", t("guided.length_standard")],
          ["more_detailed", t("guided.length_more_detailed")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={length === v ? "default" : "outline"}
            data-testid={`guided-contract-help-length-${v}`}
            onClick={() => onLengthChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_emphasis")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["scheduling", t("guided.emphasis.contract.scheduling")],
          ["concealed", t("guided.emphasis.contract.concealed")],
          ["customer_responsibility", t("guided.emphasis.contract.customer_responsibility")],
          ["materials", t("guided.emphasis.contract.materials")],
          ["cleanup", t("guided.emphasis.contract.cleanup")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={emphasize.includes(v) ? "default" : "outline"}
            data-testid={`guided-contract-help-emphasize-${v}`}
            onClick={() => onToggleEmphasize(v)}>{label}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};

const ContractGuidedPanel = ({
  family,                 // "contract"
  userId,
  companyProfile,
  guidedRecord,
  savedWording,
  projectState,          // saved project state, e.g. "CA"
  onSaveGuided,
  onSetLater,
  onHandleOutside,
  onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  const { t, lang } = useLang();
  const questions = getFamilyQuestions("contract", lang);
  const allowlist = CONTRACT_ALLOWLIST;
  const baseline = guidedBaselineFromRecord(guidedRecord);
  // Slice 2C5B · Browser-QA fix (Contract opening choice + false dirty).
  //   Effective handling mode drives the opening surface:
  //     "unselected" · no prior save OR prior save was "later"     → four-choice screen
  //     "recommended" · saved generated (or session-selected here) → questionnaire
  //     "existing"    · session-selected "Use my existing contract" → raw editor bridge
  //     "outside"     · saved outside_jobclose_ai                   → resolved-outside surface
  //     "later"       · reserved (opens four-choice on next Set-up-now)
  const savedMode = (() => {
    const ws = guidedRecord?.wording_source || null;
    if (ws === "generated" || ws === "customized") return "recommended";
    if (ws === "outside_jobclose_ai") return "outside";
    if (ws === "legacy") return "existing";
    return "unselected";   // null, "later", anything else
  })();
  const [mode, setMode] = useState(savedMode);
  const modeSelected = mode !== "unselected";

  // Answers hydrate ONLY once a mode is selected. Merely opening
  // Contract must not seed recommended defaults (that would cause a
  // false dirty state against the saved Later baseline).
  const [answers, setAnswers] = useState(() => {
    const persisted = readGuidedDraft(userId, "contract");
    if (persisted && persisted.answers) return persisted.answers;
    if (savedMode === "recommended") {
      return { ...emptyAnswersForFamily("contract"), ...baseline.answers };
    }
    // "unselected" / "outside" / "existing" / "later" — no draft seed.
    return { ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const persisted = readGuidedDraft(userId, "contract");
    return (persisted && persisted.adjustments) || {};
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Live preview
  const gen = useMemo(() => {
    const base = generateContractWording({
      companyName: companyProfile?.company_name || "",
      phone: companyProfile?.phone || "", email: companyProfile?.email || "",
      projectState: projectState || "",
      answers,
      lang,
    });
    return { ...base, wording: applyHelpMeChangeItContract(base.wording, adjustments) };
  }, [answers, adjustments, companyProfile, projectState, lang]);

  // Draft persistence — only while a mode is selected. Merely opening
  // Contract in the four-choice screen writes NOTHING to localStorage.
  useEffect(() => {
    if (!userId) return;
    if (!modeSelected) { clearGuidedDraft(userId, "contract"); return; }
    const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
    const dirty = answersDifferMeaningfully(answers, savedAnswers, "contract")
                  || Object.keys(adjustments || {}).length > 0;
    if (dirty) {
      writeGuidedDraft(userId, "contract", { answers, adjustments });
    } else {
      clearGuidedDraft(userId, "contract");
    }
  }, [userId, answers, adjustments, guidedRecord, modeSelected]);

  const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
  // Dirty state only makes sense when a mode is selected. Otherwise
  // opening Contract must never surface UNSAVED CHANGES / Discard.
  const dirty = modeSelected
    ? (isGuidedDirty(
        { answers, adjustments },
        { answers: savedAnswers, updated_at: baseline.updated_at,
          wording_source: baseline.wording_source },
        "contract",
      ) || mode !== savedMode)
    : false;

  const missingCompany = !companyProfile?.company_name;
  const canUseRecommended = !missingCompany && !busy;

  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError("");
    setBusy(true);
    try {
      // Slice 2C5B · Save exact previewed Contract wording + answers
      // (including any Help-me-change-it tone override — Help writes
      // to answers.tone directly). Length + emphasize live in local
      // adjustments and are already baked into resolved_fields via
      // applyHelpMeChangeItContract, so they persist through the
      // document_defaults baseline.
      await onSaveGuided({
        answers,
        action: "use_recommended",
        wording_source: "generated",
        resolved_fields: gen.wording,
      });
      // Slice 2C5B · session-selected mode is now the persisted mode.
      setMode("recommended");
      setAdjustments({});
      clearGuidedDraft(userId, "contract");
    } catch (e) {
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = () => {
    // Slice 2C5B · Discard returns to the previously saved handling state.
    //   * If nothing was ever saved (savedMode === "unselected"), we
    //     drop back to the four-choice screen and clear all local state.
    //   * If a mode was saved (later/recommended/existing/outside), we
    //     restore that mode and rehydrate answers from the saved baseline.
    setMode(savedMode);
    setAnswers({ ...baseline.answers });
    setAdjustments({});
    clearGuidedDraft(userId, "contract");
    setError("");
  };

  // -- First-choice screen ---------------------------------------------------
  // Rendered whenever the effective mode has NOT yet been selected this
  // session. Covers: fresh Contract (no prior save), Later-saved
  // Contract with no handling choice, and Discard-back-to-unselected.
  if (!modeSelected) {
    return (
      <div className="space-y-3" data-testid="guided-contract-first-choice">
        <div className="text-sm font-semibold">How do you want to handle your contracts?</div>
        <div className="grid gap-2">
          <Button variant="default" data-testid="guided-contract-first-use-recommended"
            onClick={() => {
              // Slice 2C5B · Recommended defaults hydrate ONLY after
              // the contractor explicitly picks Use recommended. The
              // dirty guard fires because `mode !== savedMode`.
              setMode("recommended");
              setAnswers({ ...emptyAnswersForFamily("contract"), ...baseline.answers });
            }}>
            Use JobClose AI recommended contract
          </Button>
          <Button variant="outline" data-testid="guided-contract-first-use-existing"
            onClick={() => onSwitchToEditor({ prefill: null, source: "use_existing" })}>
            Use my existing contract
          </Button>
          <Button variant="outline" data-testid="guided-contract-first-handle-outside"
            onClick={() => onHandleOutside()}>
            Handle contracts outside JobClose AI
          </Button>
          <Button variant="ghost" data-testid="guided-contract-first-set-later"
            onClick={() => onSetLater()}>
            Set up later
          </Button>
        </div>
      </div>
    );
  }

  // -- Main Contract guided experience --------------------------------------
  return (
    <div className="space-y-4" data-testid="guided-contract-panel">
      {missingCompany && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">{t("guided.company_incomplete")}</div>
          <div>Please finish your Business & Trades setup before generating Contract wording.</div>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={onNavigateToBusinessAndTrades}
            data-testid="guided-contract-goto-business">
            Open Business & Trades
          </Button>
        </div>
      )}
      <div className="space-y-3" data-testid="guided-contract-questions">
        {questions.map((q) => {
          if (q.kind === "chip") {
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-contract-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((c) => (
                    <Button key={c.value} size="sm"
                      variant={answers[q.key] === c.value ? "default" : "outline"}
                      data-testid={`guided-contract-${q.id}-${c.value}`}
                      onClick={() => setAnswers((a) => setAnswer(a, q.key, c.value))}>
                      {c.label}
                    </Button>
                  ))}
                </div>
              </div>
            );
          }
          if (q.kind === "checkbox_multiselect") {
            const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-contract-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                <div className="flex flex-col gap-1">
                  {q.choices.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={cur.includes(c.value)}
                        data-testid={`guided-contract-${q.id}-${c.value}`}
                        onCheckedChange={() => setAnswers((a) =>
                          toggleMultiSelect(a, q.key, c.value,
                            { mutuallyExclusive: q.mutuallyExclusive }))}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Preview */}
      <div className="rounded-md border p-3 space-y-2" data-testid="guided-contract-preview">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.recommended_contract_wording")}</div>
        {allowlist.map((k) => (
          <div key={k} className="text-sm whitespace-pre-wrap" data-testid={`guided-contract-preview-${k}`}>
            {gen.wording[k] || ""}
          </div>
        ))}
      </div>

      {/* Help me change it — collapsed until requested */}
      {!helpOpen && (
        <Button variant="outline" size="sm"
          onClick={() => setHelpOpen(true)}
          aria-expanded={false}
          data-testid="guided-contract-help-open">
          Help me change it
          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      )}
      {helpOpen && (
        <ContractHelpPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((prev) => ({ ...prev, length: l }))}
          emphasize={adjustments.emphasize || []}
          onToggleEmphasize={(v) => setAdjustments((prev) => {
            const cur = new Set(prev.emphasize || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            return { ...prev, emphasize: Array.from(cur) };
          })}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={doUseRecommended} disabled={!canUseRecommended}
          data-testid="guided-contract-use-recommended">
          Use JobClose AI recommended wording
        </Button>
        <Button variant="outline"
          onClick={() => onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" })}
          data-testid="guided-contract-edit-wording">
          Edit wording
        </Button>
        <Button variant="outline"
          onClick={() => onHandleOutside()}
          data-testid="guided-contract-handle-outside">
          Handle contracts outside JobClose AI
        </Button>
        <Button variant="outline" size="sm"
          onClick={() => onSetLater()}
          data-testid="guided-contract-set-later">
          Set up later
        </Button>
      </div>

      {dirty && (
        <div className="flex items-center gap-3 text-xs" data-testid="guided-contract-dirty-row">
          <Badge variant="secondary">{t("guided.unsaved_changes")}</Badge>
          <Button size="sm" variant="ghost" onClick={doDiscard}
            data-testid="guided-contract-discard-draft">
            Discard draft
          </Button>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600" data-testid="guided-contract-error">{error}</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Slice 2C5B · Pass 2 · GuidedInvoicePanel — practical Invoice defaults.
// Reuses the shared guided-family primitives (draft store, ordered save,
// active-family expansion). No new save/draft/expansion system; no payment-
// processing integration; no automatic invoice send or payment collection.
// ---------------------------------------------------------------------------
const INVOICE_ALLOWLIST = ["invoice_notes", "payment_instructions"];

const InvoiceHelpPanel = ({
  tone, onToneChange,
  length, onLengthChange,
  emphasize, onToggleEmphasize,
  onClose,
}) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-invoice-help-panel">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
      <Button size="sm" variant="ghost" onClick={onClose} data-testid="guided-invoice-help-close">{t("guided.close")}</Button>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.tone")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["friendly", t("guided.tone_friendly")],
          ["professional", t("guided.tone_professional")],
          ["straightforward", t("guided.tone_straightforward")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={tone === v ? "default" : "outline"}
            data-testid={`guided-invoice-help-tone-${v}`}
            onClick={() => onToneChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.length")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["shorter", t("guided.length_shorter")],
          ["standard", t("guided.length_standard")],
          ["more_detailed", t("guided.length_more_detailed")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={length === v ? "default" : "outline"}
            data-testid={`guided-invoice-help-length-${v}`}
            onClick={() => onLengthChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_emphasis")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["amount_due",   t("guided.emphasis.invoice.amount_due")],
          ["overdue",      t("guided.emphasis.invoice.overdue")],
          ["instructions", t("guided.emphasis.invoice.instructions")],
          ["thank_you",    t("guided.emphasis.invoice.thank_you")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={emphasize.includes(v) ? "default" : "outline"}
            data-testid={`guided-invoice-help-emphasize-${v}`}
            onClick={() => onToggleEmphasize(v)}>{label}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};

const InvoiceGuidedPanel = ({
  family,                    // "invoice"
  userId,
  companyProfile,
  guidedRecord,
  savedWording,              // reserved for legacy branch (Slice 2C5B Pass 2 has none)
  approvedProposalPaymentTerm = "",
  approvedContractPaymentTerm = "",
  approvedProposalLateFeeTerm = "",
  approvedContractLateFeeTerm = "",
  onSaveGuided,
  onSetLater,
  onHandleOutside,
  onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  void family; void savedWording;
  const { t, lang } = useLang();
  const questions  = getFamilyQuestions("invoice", lang);
  const allowlist  = INVOICE_ALLOWLIST;
  const baseline   = guidedBaselineFromRecord(guidedRecord);

  // Effective handling mode drives the opening surface (mirrors Contract).
  const savedMode = (() => {
    const ws = guidedRecord?.wording_source || null;
    if (ws === "generated" || ws === "customized") return "recommended";
    if (ws === "outside_jobclose_ai") return "outside";
    if (ws === "legacy") return "existing";
    return "unselected";
  })();
  const [mode, setMode] = useState(savedMode);
  const modeSelected = mode !== "unselected";

  const [answers, setAnswers] = useState(() => {
    const persisted = readGuidedDraft(userId, "invoice");
    if (persisted && persisted.answers) return persisted.answers;
    if (savedMode === "recommended") {
      return { ...emptyAnswersForFamily("invoice"), ...baseline.answers };
    }
    return { ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const persisted = readGuidedDraft(userId, "invoice");
    if (persisted && persisted.adjustments) return persisted.adjustments;
    // Slice 2C5B · Pass 2 · Invoice browser-QA fix — hydrate approved
    // helper adjustments from the guided companion so reopening after
    // refresh reproduces the exact adjusted recommendation cleanly.
    return { ...(baseline.adjustments || {}) };
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Live preview
  const gen = useMemo(() => {
    const base = generateInvoiceWording({
      companyName: companyProfile?.company_name || "",
      phone: companyProfile?.phone || "",
      email: companyProfile?.email || "",
      licenseInfo: companyProfile?.license_info || companyProfile?.license || "",
      approvedProposalPaymentTerm, approvedContractPaymentTerm,
      approvedProposalLateFeeTerm, approvedContractLateFeeTerm,
      answers,
      lang,
    });
    if (!base.ok || !base.wording) return base;
    return { ...base, wording: applyHelpMeChangeItInvoice(base.wording, adjustments) };
  }, [answers, adjustments, companyProfile,
      approvedProposalPaymentTerm, approvedContractPaymentTerm,
      approvedProposalLateFeeTerm, approvedContractLateFeeTerm, lang]);

  // Draft persistence — only while a mode is selected.
  useEffect(() => {
    if (!userId) return;
    if (!modeSelected) { clearGuidedDraft(userId, "invoice"); return; }
    const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
    const savedAdjustments = (guidedRecord && guidedRecord.adjustments) || {};
    let adjustmentsDiffer = false;
    try {
      adjustmentsDiffer = JSON.stringify(adjustments || {}) !==
                          JSON.stringify(savedAdjustments || {});
    } catch (_e) {
      adjustmentsDiffer = Object.keys(adjustments || {}).length > 0;
    }
    const dirty = answersDifferMeaningfully(answers, savedAnswers, "invoice")
                  || adjustmentsDiffer;
    if (dirty) {
      writeGuidedDraft(userId, "invoice", { answers, adjustments });
    } else {
      clearGuidedDraft(userId, "invoice");
    }
  }, [userId, answers, adjustments, guidedRecord, modeSelected]);

  const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
  const dirty = modeSelected
    ? (isGuidedDirty(
        { answers, adjustments },
        { answers: savedAnswers,
          adjustments: baseline.adjustments || {},
          updated_at: baseline.updated_at,
          wording_source: baseline.wording_source },
        "invoice",
      ) || mode !== savedMode)
    : false;

  const missingCompany = !companyProfile?.company_name;
  // Payment methods must have at least one selection; card contradiction
  // is prevented by the reducer (deselecting card drops card_fee_handling).
  const methods = Array.isArray(answers.payment_methods) ? answers.payment_methods : [];
  const paymentMethodsOk = methods.length >= 1;
  const canUseRecommended =
    !missingCompany && !busy && !!gen.ok && paymentMethodsOk;

  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError("");
    setBusy(true);
    try {
      // Slice 2C5B · Pass 2 · Invoice browser-QA fix — the payload now
      // includes the ACTIVE helper adjustments so the server-owned
      // companion can reproduce the exact adjusted wording on refresh.
      // The resolved_fields carry the exact displayed wording (gen.wording),
      // so the persisted document_defaults matches what the contractor
      // sees on screen. Adjustments are NOT reset locally after save —
      // resetting them would cause gen.wording to regenerate without the
      // adjustment, mismatching docValues and producing a false dirty
      // state the next time Edit wording is clicked.
      await onSaveGuided({
        answers,
        action: "use_recommended",
        wording_source: "generated",
        resolved_fields: gen.wording,
        adjustments,
      });
      setMode("recommended");
      clearGuidedDraft(userId, "invoice");
    } catch (e) {
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = () => {
    setMode(savedMode);
    setAnswers({ ...baseline.answers });
    // Reset to the SAVED helper adjustments (not {}), so discarding a
    // draft returns to the approved adjusted recommendation cleanly.
    setAdjustments({ ...(baseline.adjustments || {}) });
    clearGuidedDraft(userId, "invoice");
    setError("");
  };

  // ---- First-choice screen ------------------------------------------------
  if (!modeSelected) {
    return (
      <div className="space-y-3" data-testid="guided-invoice-first-choice">
        <div className="text-sm font-semibold">How do you want to handle your invoices?</div>
        <div className="grid gap-2">
          <Button variant="default" data-testid="guided-invoice-first-use-recommended"
            onClick={() => {
              // Recommended defaults hydrate ONLY on explicit selection.
              setMode("recommended");
              setAnswers({ ...emptyAnswersForFamily("invoice"), ...baseline.answers });
            }}>
            Use JobClose AI recommended invoice setup
          </Button>
          <Button variant="outline" data-testid="guided-invoice-first-use-existing"
            onClick={() => onSwitchToEditor({ prefill: null, source: "use_existing" })}>
            Use my existing invoice wording
          </Button>
          <Button variant="outline" data-testid="guided-invoice-first-handle-outside"
            onClick={() => onHandleOutside()}>
            Handle invoices outside JobClose AI
          </Button>
          <Button variant="ghost" data-testid="guided-invoice-first-set-later"
            onClick={() => onSetLater()}>
            Set up later
          </Button>
        </div>
      </div>
    );
  }

  // ---- Main Invoice guided experience -------------------------------------
  return (
    <div className="space-y-4" data-testid="guided-invoice-panel">
      {missingCompany && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">{t("guided.company_incomplete")}</div>
          <div>Please finish your Business &amp; Trades setup before generating Invoice wording.</div>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={onNavigateToBusinessAndTrades}
            data-testid="guided-invoice-goto-business">
            Open Business &amp; Trades
          </Button>
        </div>
      )}

      <div className="space-y-3" data-testid="guided-invoice-questions">
        {questions.map((q) => {
          // Conditional visibility (Q4 card-fee, retainage).
          if (q.dependsOn && typeof q.dependsOn.predicate === "function") {
            if (!q.dependsOn.predicate(answers[q.dependsOn.key])) return null;
          }
          if (q.kind === "chip") {
            const current = answers[q.key] ?? q.defaultValue;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-invoice-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((c) => (
                    <Button key={c.value} size="sm"
                      variant={current === c.value ? "default" : "outline"}
                      data-testid={`guided-invoice-${q.id}-${c.value}`}
                      onClick={() => setAnswers((a) => setAnswer(a, q.key, c.value))}>
                      {c.label}
                    </Button>
                  ))}
                </div>
                {(q.conditionalInputs || []).map((ci) =>
                  current === ci.when ? (
                    <div key={ci.key} className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground w-40">{ci.label}</div>
                      <Input type={ci.kind === "number" ? "number" : "text"}
                        value={answers[ci.key] ?? ""}
                        min={ci.min} max={ci.max}
                        placeholder={ci.placeholder || ""}
                        data-testid={`guided-invoice-${q.id}-${ci.key}`}
                        onChange={(e) => setAnswers((a) =>
                          setAnswer(a, ci.key, e.target.value === ""
                            ? undefined
                            : (ci.kind === "number" ? Number(e.target.value) : e.target.value)))}
                      />
                    </div>
                  ) : null
                )}
              </div>
            );
          }
          if (q.kind === "checkbox_multiselect") {
            const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
            const belowMin = q.minSelected && cur.length < q.minSelected;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-invoice-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                <div className="flex flex-col gap-1">
                  {q.choices.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={cur.includes(c.value)}
                        data-testid={`guided-invoice-${q.id}-${c.value}`}
                        onCheckedChange={() => setAnswers((a) =>
                          toggleMultiSelect(a, q.key, c.value))}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
                {belowMin && (
                  <div className="text-xs text-amber-800" data-testid={`guided-invoice-${q.id}-min-warning`}>
                    Select at least one option before saving.
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Preview */}
      {gen.ok && (
        <div className="rounded-md border p-3 space-y-2" data-testid="guided-invoice-preview">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("guided.recommended_invoice_wording")}
          </div>
          {allowlist.map((k) => {
            const v = gen.wording[k];
            if (!v) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap" data-testid={`guided-invoice-preview-${k}`}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, " ")}</div>
                {v}
              </div>
            );
          })}
        </div>
      )}

      {/* Help me change it — collapsed until requested */}
      {!helpOpen && (
        <Button variant="outline" size="sm"
          onClick={() => setHelpOpen(true)}
          aria-expanded={false}
          data-testid="guided-invoice-help-open">
          Help me change it
          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      )}
      {helpOpen && (
        <InvoiceHelpPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((prev) => ({ ...prev, length: l }))}
          emphasize={adjustments.emphasize || []}
          onToggleEmphasize={(v) => setAdjustments((prev) => {
            const cur = new Set(prev.emphasize || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            return { ...prev, emphasize: Array.from(cur) };
          })}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={doUseRecommended} disabled={!canUseRecommended}
          data-testid="guided-invoice-use-recommended">
          Use JobClose AI recommended wording
        </Button>
        <Button variant="outline"
          onClick={() => onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" })}
          disabled={!gen.ok}
          data-testid="guided-invoice-edit-wording">
          Edit wording
        </Button>
        <Button variant="outline"
          onClick={() => onHandleOutside()}
          data-testid="guided-invoice-handle-outside">
          Handle invoices outside JobClose AI
        </Button>
        <Button variant="outline" size="sm"
          onClick={() => onSetLater()}
          data-testid="guided-invoice-set-later">
          Set up later
        </Button>
      </div>

      {dirty && (
        <div className="flex items-center gap-3 text-xs" data-testid="guided-invoice-dirty-row">
          <Badge variant="secondary">{t("guided.unsaved_changes")}</Badge>
          <Button size="sm" variant="ghost" onClick={doDiscard}
            data-testid="guided-invoice-discard-draft">
            Discard draft
          </Button>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600" data-testid="guided-invoice-error">{error}</div>
      )}
    </div>
  );
};



// ---------------------------------------------------------------------------
// Slice 2C5B · Pass 3 · CustomerUpdateGuidedPanel — customer-update defaults.
// Reuses the shared guided-family primitives (draft store, ordered save,
// active-family expansion, adjustment round-trip). No new save/draft/
// expansion system; no messaging / SMS / email / portal integration;
// no automatic customer send/share/notify behavior anywhere in the file.
// ---------------------------------------------------------------------------
const CUSTOMER_UPDATE_ALLOWLIST = [
  "customer_update_signature", "review_request", "referral_request",
];

const CustomerUpdateHelpPanel = ({
  tone, onToneChange,
  length, onLengthChange,
  emphasize, onToggleEmphasize,
  onClose,
}) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-customer_update-help-panel">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
      <Button size="sm" variant="ghost" onClick={onClose}
        data-testid="guided-customer_update-help-close">{t("guided.close")}</Button>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.tone")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["friendly", t("guided.tone_friendly")],
          ["professional", t("guided.tone_professional")],
          ["straightforward", t("guided.tone_straightforward")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={tone === v ? "default" : "outline"}
            data-testid={`guided-customer_update-help-tone-${v}`}
            onClick={() => onToneChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.length")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["shorter", t("guided.length_shorter")],
          ["standard", t("guided.length_standard")],
          ["more_detailed", t("guided.length_more_detailed")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={length === v ? "default" : "outline"}
            data-testid={`guided-customer_update-help-length-${v}`}
            onClick={() => onLengthChange(v)}>{label}</Button>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_emphasis")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["clearer_next_steps",     t("guided.emphasis.customer_update.clearer_next_steps")],
          ["warmer_delay",           t("guided.emphasis.customer_update.warmer_delay")],
          ["text_message_style",     t("guided.emphasis.customer_update.text_message_style")],
          ["warmer_review_referral", t("guided.emphasis.customer_update.warmer_review_referral")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={emphasize.includes(v) ? "default" : "outline"}
            data-testid={`guided-customer_update-help-emphasize-${v}`}
            onClick={() => onToggleEmphasize(v)}>{label}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};

const CustomerUpdateGuidedPanel = ({
  family,
  userId,
  companyProfile,
  guidedRecord,
  onSaveGuided,
  onSetLater,
  onHandleOutside,
  onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  void family;
  const { t, lang } = useLang();
  const questions  = getFamilyQuestions("customer_update", lang);
  const allowlist  = CUSTOMER_UPDATE_ALLOWLIST;
  const baseline   = guidedBaselineFromRecord(guidedRecord);

  const savedMode = (() => {
    const ws = guidedRecord?.wording_source || null;
    if (ws === "generated" || ws === "customized") return "recommended";
    if (ws === "outside_jobclose_ai") return "outside";
    if (ws === "legacy") return "existing";
    return "unselected";
  })();
  const [mode, setMode] = useState(savedMode);
  const modeSelected = mode !== "unselected";

  const [answers, setAnswers] = useState(() => {
    const persisted = readGuidedDraft(userId, "customer_update");
    if (persisted && persisted.answers) return persisted.answers;
    if (savedMode === "recommended") {
      return { ...emptyAnswersForFamily("customer_update"), ...baseline.answers };
    }
    return { ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const persisted = readGuidedDraft(userId, "customer_update");
    if (persisted && persisted.adjustments) return persisted.adjustments;
    return { ...(baseline.adjustments || {}) };
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const gen = useMemo(() => {
    const base = generateCustomerUpdateWording({
      companyName: companyProfile?.company_name || "",
      phone: companyProfile?.phone || "",
      email: companyProfile?.email || "",
      answers,
    });
    if (!base.ok || !base.wording) return base;
    // Slice 2C5B Pass 3 · Customer Update browser-QA localization fix.
    //   Every helper refinement must match the customer-facing language
    //   the base wording was generated in. Explicit Spanish selection
    //   must produce fully Spanish customer-facing wording — never a
    //   mixed-language update. Dynamic paths (preferred / choose_each)
    //   continue to render English previews with the UI-only note.
    const lang = answers.language_rule === "es" ? "es" : "en";
    return { ...base, wording: applyHelpMeChangeItCustomerUpdate(base.wording, adjustments, { lang }) };
  }, [answers, adjustments, companyProfile]);

  useEffect(() => {
    if (!userId) return;
    if (!modeSelected) { clearGuidedDraft(userId, "customer_update"); return; }
    const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
    const savedAdjustments = (guidedRecord && guidedRecord.adjustments) || {};
    let adjustmentsDiffer = false;
    try {
      adjustmentsDiffer = JSON.stringify(adjustments || {}) !==
                          JSON.stringify(savedAdjustments || {});
    } catch (_e) {
      adjustmentsDiffer = Object.keys(adjustments || {}).length > 0;
    }
    const dirty = answersDifferMeaningfully(answers, savedAnswers, "customer_update")
                  || adjustmentsDiffer;
    if (dirty) {
      writeGuidedDraft(userId, "customer_update", { answers, adjustments });
    } else {
      clearGuidedDraft(userId, "customer_update");
    }
  }, [userId, answers, adjustments, guidedRecord, modeSelected]);

  const savedAnswers = (guidedRecord && guidedRecord.answers) || {};
  const dirty = modeSelected
    ? (isGuidedDirty(
        { answers, adjustments },
        { answers: savedAnswers,
          adjustments: baseline.adjustments || {},
          updated_at: baseline.updated_at,
          wording_source: baseline.wording_source },
        "customer_update",
      ) || mode !== savedMode)
    : false;

  const missingCompany = !companyProfile?.company_name;
  const milestones = Array.isArray(answers.milestones) ? answers.milestones : [];
  const milestonesOk = milestones.length >= 1;
  const canUseRecommended =
    !missingCompany && !busy && !!gen.ok && milestonesOk;

  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError("");
    setBusy(true);
    try {
      await onSaveGuided({
        answers,
        action: "use_recommended",
        wording_source: "generated",
        resolved_fields: gen.wording,
        adjustments,
      });
      setMode("recommended");
      clearGuidedDraft(userId, "customer_update");
    } catch (e) {
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = () => {
    setMode(savedMode);
    setAnswers({ ...baseline.answers });
    setAdjustments({ ...(baseline.adjustments || {}) });
    clearGuidedDraft(userId, "customer_update");
    setError("");
  };

  if (!modeSelected) {
    return (
      <div className="space-y-3" data-testid="guided-customer_update-first-choice">
        <div className="text-sm font-semibold">How do you want to handle customer updates?</div>
        <div className="grid gap-2">
          <Button variant="default" data-testid="guided-customer_update-first-use-recommended"
            onClick={() => {
              setMode("recommended");
              setAnswers({ ...emptyAnswersForFamily("customer_update"), ...baseline.answers });
            }}>
            Use JobClose AI recommended customer update setup
          </Button>
          <Button variant="outline" data-testid="guided-customer_update-first-use-existing"
            onClick={() => onSwitchToEditor({ prefill: null, source: "use_existing" })}>
            Use my existing customer update wording
          </Button>
          <Button variant="outline" data-testid="guided-customer_update-first-handle-outside"
            onClick={() => onHandleOutside()}>
            Handle customer updates outside JobClose AI
          </Button>
          <Button variant="outline" size="sm" data-testid="guided-customer_update-first-set-later"
            onClick={() => onSetLater()}>
            Set up later
          </Button>
        </div>
      </div>
    );
  }

  const showLangUiNote =
    answers.language_rule === "preferred" || answers.language_rule === "choose_each";

  return (
    <div className="space-y-4" data-testid="guided-customer_update-panel">
      {missingCompany && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">{t("guided.company_incomplete")}</div>
          <div>Please finish your Business &amp; Trades setup before generating Customer Update wording.</div>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={onNavigateToBusinessAndTrades}
            data-testid="guided-customer_update-goto-business">
            Open Business &amp; Trades
          </Button>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
           data-testid="guided-customer_update-safety-note">
        JobClose AI prepares customer-update drafts. You review and approve every
        update before anything is sent, shared, or published.
      </div>

      <div className="space-y-3" data-testid="guided-customer_update-questions">
        {questions.map((q) => {
          if (q.kind === "chip") {
            const current = answers[q.key] ?? q.defaultValue;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-customer_update-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (
                  <div className="text-xs text-muted-foreground"
                       data-testid={`guided-customer_update-help-${q.id}`}>
                    {q.helpText}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((c) => (
                    <Button key={c.value} size="sm"
                      variant={current === c.value ? "default" : "outline"}
                      data-testid={`guided-customer_update-${q.id}-${c.value}`}
                      onClick={() => setAnswers((a) => setAnswer(a, q.key, c.value))}>
                      {c.label}
                    </Button>
                  ))}
                </div>
                {q.id === "language_rule" && showLangUiNote && (
                  <div className="text-xs italic text-muted-foreground"
                       data-testid="guided-customer_update-language-ui-note">
                    Preview shown in English. The actual draft language is resolved at job time from the customer's preference. This note is not saved as customer-facing wording.
                  </div>
                )}
              </div>
            );
          }
          if (q.kind === "checkbox_multiselect") {
            const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
            const belowMin = q.minSelected && cur.length < q.minSelected;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-customer_update-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (
                  <div className="text-xs text-muted-foreground"
                       data-testid={`guided-customer_update-help-${q.id}`}>
                    {q.helpText}
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  {q.choices.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={cur.includes(c.value)}
                        data-testid={`guided-customer_update-${q.id}-${c.value}`}
                        onCheckedChange={() => setAnswers((a) =>
                          toggleMultiSelect(a, q.key, c.value, {
                            mutuallyExclusive: q.mutuallyExclusive || null,
                          }))}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
                {belowMin && (
                  <div className="text-xs text-amber-800"
                       data-testid={`guided-customer_update-${q.id}-min-warning`}>
                    Select at least one option before saving.
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {gen.ok && (
        <div className="rounded-md border p-3 space-y-2"
             data-testid="guided-customer_update-preview">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("guided.recommended_customer_update_wording")}
          </div>
          {allowlist.map((k) => {
            const v = gen.wording[k];
            if (!v) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap"
                   data-testid={`guided-customer_update-preview-${k}`}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {k.replace(/_/g, " ")}
                </div>
                {v}
              </div>
            );
          })}
        </div>
      )}

      {!helpOpen && (
        <Button variant="outline" size="sm"
          onClick={() => setHelpOpen(true)}
          aria-expanded={false}
          data-testid="guided-customer_update-help-open">
          Help me change it
          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      )}
      {helpOpen && (
        <CustomerUpdateHelpPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((prev) => ({ ...prev, length: l }))}
          emphasize={adjustments.emphasize || []}
          onToggleEmphasize={(v) => setAdjustments((prev) => {
            const cur = new Set(prev.emphasize || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            return { ...prev, emphasize: Array.from(cur) };
          })}
          onClose={() => setHelpOpen(false)}
        />
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={doUseRecommended} disabled={!canUseRecommended}
          data-testid="guided-customer_update-use-recommended">
          Use JobClose AI recommended wording
        </Button>
        <Button variant="outline"
          onClick={() => onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" })}
          disabled={!gen.ok}
          data-testid="guided-customer_update-edit-wording">
          Edit wording
        </Button>
        <Button variant="outline"
          onClick={() => onHandleOutside()}
          data-testid="guided-customer_update-handle-outside">
          Handle customer updates outside JobClose AI
        </Button>
        <Button variant="outline" size="sm"
          onClick={() => onSetLater()}
          data-testid="guided-customer_update-set-later">
          Set up later
        </Button>
      </div>

      {dirty && (
        <div className="flex items-center gap-3 text-xs"
             data-testid="guided-customer_update-dirty-row">
          <Badge variant="secondary">{t("guided.unsaved_changes")}</Badge>
          <Button size="sm" variant="ghost" onClick={doDiscard}
            data-testid="guided-customer_update-discard-draft">
            Discard draft
          </Button>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600"
             data-testid="guided-customer_update-error">{error}</div>
      )}
    </div>
  );
};



// ---------------------------------------------------------------------------
// Slice 2C5C · Pass 1 · CloseoutGuidedPanel — closeout defaults + wording.
// Reuses the shared guided primitives, saved-adjustment round trip, and
// shared action-button treatment. No job-closing, signature, portal-publish,
// email, SMS, provider, or auto-share integration anywhere in the file.
// ---------------------------------------------------------------------------
const CLOSEOUT_ALLOWLIST = ["closeout_notes_template", "maintenance_notes"];

const CloseoutHelpPanel = ({
  tone, onToneChange, length, onLengthChange,
  emphasize, onToggleEmphasize, onClose,
}) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-closeout-help-panel">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
      <Button size="sm" variant="ghost" onClick={onClose}
        data-testid="guided-closeout-help-close">{t("guided.close")}</Button>
    </div>
    {[[t("guided.tone"), [["friendly",t("guided.tone_friendly")],["professional",t("guided.tone_professional")],["straightforward",t("guided.tone_straightforward")]], tone, onToneChange, "tone"],
      [t("guided.length"), [["shorter",t("guided.length_shorter")],["standard",t("guided.length_standard")],["more_detailed",t("guided.length_more_detailed")]], length, onLengthChange, "length"],
    ].map(([lbl, opts, cur, cb, k]) => (
      <div key={k} className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{lbl}</div>
        <div className="flex flex-wrap gap-2">
          {opts.map(([v, label]) => (
            <Button key={v} size="sm"
              variant={cur === v ? "default" : "outline"}
              data-testid={`guided-closeout-help-${k}-${v}`}
              onClick={() => cb(v)}>{label}</Button>
          ))}
        </div>
      </div>
    ))}
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_emphasis")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["clearer_final_next_steps",   t("guided.emphasis.closeout.clearer_final_next_steps")],
          ["stronger_punch_list_clarity",t("guided.emphasis.closeout.stronger_punch_list_clarity")],
          ["warmer_completion",          t("guided.emphasis.closeout.warmer_completion")],
          ["clearer_ack",                t("guided.emphasis.closeout.clearer_ack")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={emphasize.includes(v) ? "default" : "outline"}
            data-testid={`guided-closeout-help-emphasize-${v}`}
            onClick={() => onToggleEmphasize(v)}>{label}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};

const CloseoutGuidedPanel = ({
  family, userId, companyProfile, guidedRecord,
  onSaveGuided, onSetLater, onHandleOutside, onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  void family;
  const { t, lang } = useLang();
  const questions = getFamilyQuestions("closeout", lang);
  const allowlist = CLOSEOUT_ALLOWLIST;
  const baseline = guidedBaselineFromRecord(guidedRecord);
  const savedMode = (() => {
    const ws = guidedRecord?.wording_source || null;
    if (ws === "generated" || ws === "customized") return "recommended";
    if (ws === "outside_jobclose_ai") return "outside";
    if (ws === "legacy") return "existing";
    return "unselected";
  })();
  const [mode, setMode] = useState(savedMode);
  const modeSelected = mode !== "unselected";
  const [answers, setAnswers] = useState(() => {
    const p = readGuidedDraft(userId, "closeout");
    if (p && p.answers) return p.answers;
    if (savedMode === "recommended")
      return { ...emptyAnswersForFamily("closeout"), ...baseline.answers };
    return { ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const p = readGuidedDraft(userId, "closeout");
    if (p && p.adjustments) return p.adjustments;
    return { ...(baseline.adjustments || {}) };
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const gen = useMemo(() => {
    const base = generateCloseoutWording({
      companyName: companyProfile?.company_name || "",
      phone: companyProfile?.phone || "",
      email: companyProfile?.email || "",
      answers,
      lang,
    });
    if (!base.ok || !base.wording) return base;
    return { ...base, wording: applyHelpMeChangeItCloseout(base.wording, adjustments) };
  }, [answers, adjustments, companyProfile, lang]);
  useEffect(() => {
    if (!userId) return;
    if (!modeSelected) { clearGuidedDraft(userId, "closeout"); return; }
    const sa = (guidedRecord && guidedRecord.answers) || {};
    const sadj = (guidedRecord && guidedRecord.adjustments) || {};
    let adjDiff = false;
    try { adjDiff = JSON.stringify(adjustments || {}) !== JSON.stringify(sadj || {}); }
    catch (_e) { adjDiff = Object.keys(adjustments || {}).length > 0; }
    const dirty = answersDifferMeaningfully(answers, sa, "closeout") || adjDiff;
    if (dirty) writeGuidedDraft(userId, "closeout", { answers, adjustments });
    else clearGuidedDraft(userId, "closeout");
  }, [userId, answers, adjustments, guidedRecord, modeSelected]);
  const sa = (guidedRecord && guidedRecord.answers) || {};
  const dirty = modeSelected
    ? (isGuidedDirty({ answers, adjustments },
        { answers: sa, adjustments: baseline.adjustments || {},
          updated_at: baseline.updated_at, wording_source: baseline.wording_source },
        "closeout") || mode !== savedMode)
    : false;
  const missingCompany = !companyProfile?.company_name;
  const pkg = Array.isArray(answers.package_items) ? answers.package_items : [];
  const canUseRecommended = !missingCompany && !busy && !!gen.ok && pkg.length >= 1;
  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError(""); setBusy(true);
    try {
      // Closeout saves defaults + wording only. Never closes a job, sends,
      // shares, publishes, or requests a signature.
      await onSaveGuided({
        answers, action: "use_recommended", wording_source: "generated",
        resolved_fields: gen.wording, adjustments,
      });
      setMode("recommended");
      clearGuidedDraft(userId, "closeout");
    } catch (e) { setError(String((e && e.message) || e)); }
    finally { setBusy(false); }
  };
  const doDiscard = () => {
    setMode(savedMode);
    setAnswers({ ...baseline.answers });
    setAdjustments({ ...(baseline.adjustments || {}) });
    clearGuidedDraft(userId, "closeout"); setError("");
  };
  if (!modeSelected) {
    return (
      <div className="space-y-3" data-testid="guided-closeout-first-choice">
        <div className="text-sm font-semibold">How do you want to handle job closeout?</div>
        <div className="grid gap-2">
          <Button variant="default" data-testid="guided-closeout-first-use-recommended"
            onClick={() => {
              setMode("recommended");
              setAnswers({ ...emptyAnswersForFamily("closeout"), ...baseline.answers });
            }}>{t("guided.use_recommended_closeout")}</Button>
          <Button variant="outline" data-testid="guided-closeout-first-use-existing"
            onClick={() => onSwitchToEditor({ prefill: null, source: "use_existing" })}>
            Use my existing closeout wording</Button>
          <Button variant="outline" data-testid="guided-closeout-first-handle-outside"
            onClick={() => onHandleOutside()}>{t("guided.handle_outside_closeout")}</Button>
          <Button variant="outline" size="sm" data-testid="guided-closeout-first-set-later"
            onClick={() => onSetLater()}>{t("guided.set_later")}</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="guided-closeout-panel">
      {missingCompany && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">{t("guided.company_incomplete")}</div>
          <div>Please finish your Business &amp; Trades setup before generating Closeout wording.</div>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={onNavigateToBusinessAndTrades}
            data-testid="guided-closeout-goto-business">Open Business &amp; Trades</Button>
        </div>
      )}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
           data-testid="guided-closeout-safety-note">
        JobClose AI prepares closeout wording only after contractor-confirmed job information.
        Nothing is automatically closed, signed, sent, shared, or published to the customer.
        Calendar dates alone are not treated as completion.
      </div>
      <div className="space-y-3" data-testid="guided-closeout-questions">
        {questions.map((q) => {
          if (q.kind === "chip") {
            const cur = answers[q.key] ?? q.defaultValue;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-closeout-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (<div className="text-xs text-muted-foreground"
                  data-testid={`guided-closeout-help-${q.id}`}>{q.helpText}</div>)}
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((c) => (
                    <Button key={c.value} size="sm"
                      variant={cur === c.value ? "default" : "outline"}
                      data-testid={`guided-closeout-${q.id}-${c.value}`}
                      onClick={() => setAnswers((a) => setAnswer(a, q.key, c.value))}>
                      {c.label}</Button>
                  ))}
                </div>
              </div>
            );
          }
          if (q.kind === "checkbox_multiselect") {
            const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
            const belowMin = q.minSelected && cur.length < q.minSelected;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-closeout-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (<div className="text-xs text-muted-foreground"
                  data-testid={`guided-closeout-help-${q.id}`}>{q.helpText}</div>)}
                <div className="flex flex-col gap-1">
                  {q.choices.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={cur.includes(c.value)}
                        data-testid={`guided-closeout-${q.id}-${c.value}`}
                        onCheckedChange={() => setAnswers((a) =>
                          toggleMultiSelect(a, q.key, c.value, {
                            mutuallyExclusive: q.mutuallyExclusive || null,
                          }))} />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
                {belowMin && (<div className="text-xs text-amber-800"
                  data-testid={`guided-closeout-${q.id}-min-warning`}>Select at least one option before saving.</div>)}
              </div>
            );
          }
          return null;
        })}
      </div>
      {gen.ok && (
        <div className="rounded-md border p-3 space-y-2" data-testid="guided-closeout-preview">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("guided.recommended_closeout_wording")}</div>
          {allowlist.map((k) => {
            const v = gen.wording[k];
            if (!v) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap"
                   data-testid={`guided-closeout-preview-${k}`}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {k.replace(/_/g, " ")}</div>
                {v}
              </div>
            );
          })}
        </div>
      )}
      {!helpOpen && (
        <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}
          aria-expanded={false} data-testid="guided-closeout-help-open">
          Help me change it
          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      )}
      {helpOpen && (
        <CloseoutHelpPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((p) => ({ ...p, length: l }))}
          emphasize={adjustments.emphasize || []}
          onToggleEmphasize={(v) => setAdjustments((p) => {
            const cur = new Set(p.emphasize || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            return { ...p, emphasize: Array.from(cur) };
          })}
          onClose={() => setHelpOpen(false)} />
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={doUseRecommended} disabled={!canUseRecommended}
          data-testid="guided-closeout-use-recommended">
          Use JobClose AI recommended wording</Button>
        <Button variant="outline"
          onClick={() => onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" })}
          disabled={!gen.ok} data-testid="guided-closeout-edit-wording">{t("guided.edit_wording")}</Button>
        <Button variant="outline" onClick={() => onHandleOutside()}
          data-testid="guided-closeout-handle-outside">{t("guided.handle_outside_closeout")}</Button>
        <Button variant="outline" size="sm" onClick={() => onSetLater()}
          data-testid="guided-closeout-set-later">{t("guided.set_later")}</Button>
      </div>
      {dirty && (
        <div className="flex items-center gap-3 text-xs" data-testid="guided-closeout-dirty-row">
          <Badge variant="secondary">{t("guided.unsaved_changes")}</Badge>
          <Button size="sm" variant="ghost" onClick={doDiscard}
            data-testid="guided-closeout-discard-draft">{t("guided.discard_draft")}</Button>
        </div>
      )}
      {error && (<div className="text-xs text-red-600"
        data-testid="guided-closeout-error">{error}</div>)}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Slice 2C5C · Pass 2 · WarrantyGuidedPanel — warranty defaults + wording.
// Writes into the three existing warranty document-default fields only.
// Never issues, activates, sends, publishes, signs, approves, or denies a
// warranty. Never guarantees manufacturer coverage. Never builds a claim
// intake workflow.
// ---------------------------------------------------------------------------
const WARRANTY_ALLOWLIST = [
  "workmanship_warranty_terms",
  "manufacturer_warranty_notes",
  "warranty_contact_notes",
];

const WarrantyHelpPanel = ({
  tone, onToneChange, length, onLengthChange,
  emphasize, onToggleEmphasize, onClose,
}) => {
  const { t } = useLang();
  return (
  <div className="rounded-md border p-3 space-y-3" data-testid="guided-warranty-help-panel">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{t("guided.help_me_change_it")}</div>
      <Button size="sm" variant="ghost" onClick={onClose}
        data-testid="guided-warranty-help-close">{t("guided.close")}</Button>
    </div>
    {[[t("guided.tone"), [["friendly",t("guided.tone_friendly")],["professional",t("guided.tone_professional")],["straightforward",t("guided.tone_straightforward")]], tone, onToneChange, "tone"],
      [t("guided.length"), [["shorter",t("guided.length_shorter")],["standard",t("guided.length_standard")],["more_detailed",t("guided.length_more_detailed")]], length, onLengthChange, "length"],
    ].map(([lbl, opts, cur, cb, k]) => (
      <div key={k} className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{lbl}</div>
        <div className="flex flex-wrap gap-2">
          {opts.map(([v, label]) => (
            <Button key={v} size="sm"
              variant={cur === v ? "default" : "outline"}
              data-testid={`guided-warranty-help-${k}-${v}`}
              onClick={() => cb(v)}>{label}</Button>
          ))}
        </div>
      </div>
    ))}
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("guided.optional_emphasis")}</div>
      <div className="flex flex-wrap gap-2">
        {[
          ["clearer_workmanship_coverage",   t("guided.emphasis.warranty.clearer_workmanship_coverage")],
          ["stronger_exclusion_clarity",     t("guided.emphasis.warranty.stronger_exclusion_clarity")],
          ["clearer_manufacturer_distinction",t("guided.emphasis.warranty.clearer_manufacturer_distinction")],
          ["clearer_warranty_request_steps", t("guided.emphasis.warranty.clearer_warranty_request_steps")],
        ].map(([v, label]) => (
          <Button key={v} size="sm"
            variant={emphasize.includes(v) ? "default" : "outline"}
            data-testid={`guided-warranty-help-emphasize-${v}`}
            onClick={() => onToggleEmphasize(v)}>{label}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};

const TradeOverridesSection = ({ activeTrades, overrides, onAdd, onUpdate, onRemove }) => {
  const { t } = useLang();
  const [newTrade, setNewTrade] = useState("");
  const [newPeriod, setNewPeriod] = useState("1_year");
  const [newCustom, setNewCustom] = useState("");
  const alreadyOverridden = new Set((overrides || []).map((o) => o.trade));
  const available = (activeTrades || []).filter((t) => !alreadyOverridden.has(t));
  return (
    <div className="space-y-2" data-testid="guided-warranty-trade-overrides">
      <div className="text-sm font-medium">Trade-specific workmanship warranty overrides</div>
      <div className="text-xs text-muted-foreground">
        Only your active selected trades may be overridden. One override per trade.
        Trades without an override inherit your standard workmanship warranty period.
      </div>
      {(overrides || []).map((o) => (
        <div key={o.trade} className="flex flex-wrap items-center gap-2"
             data-testid={`guided-warranty-override-row-${o.trade}`}>
          <div className="text-sm font-medium min-w-[120px]">{o.trade}</div>
          <select value={o.period}
            data-testid={`guided-warranty-override-period-${o.trade}`}
            onChange={(e) => onUpdate(o.trade, e.target.value,
              e.target.value === "custom" ? (o.custom_period || "") : null)}
            className="rounded border px-2 py-1 text-sm">
            {WARRANTY_PERIOD_VALUES.map((v) => (
              <option key={v} value={v}>{WARRANTY_PERIOD_LABELS[v]}</option>
            ))}
          </select>
          {o.period === "custom" && (
            <input
              value={o.custom_period || ""}
              data-testid={`guided-warranty-override-custom-${o.trade}`}
              onChange={(e) => onUpdate(o.trade, "custom", e.target.value)}
              placeholder="e.g. 3 years"
              className="rounded border px-2 py-1 text-sm min-w-[180px]"
              maxLength={200} />
          )}
          <Button size="sm" variant="ghost" onClick={() => onRemove(o.trade)}
            data-testid={`guided-warranty-override-remove-${o.trade}`}>{t("guided.remove")}</Button>
        </div>
      ))}
      {available.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t"
             data-testid="guided-warranty-override-add-row">
          <select value={newTrade}
            data-testid="guided-warranty-override-new-trade"
            onChange={(e) => setNewTrade(e.target.value)}
            className="rounded border px-2 py-1 text-sm">
            <option value="">Choose a trade…</option>
            {available.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
          <select value={newPeriod}
            data-testid="guided-warranty-override-new-period"
            onChange={(e) => setNewPeriod(e.target.value)}
            className="rounded border px-2 py-1 text-sm">
            {WARRANTY_PERIOD_VALUES.map((v) => (
              <option key={v} value={v}>{WARRANTY_PERIOD_LABELS[v]}</option>
            ))}
          </select>
          {newPeriod === "custom" && (
            <input value={newCustom}
              data-testid="guided-warranty-override-new-custom"
              onChange={(e) => setNewCustom(e.target.value)}
              placeholder={t("guided.custom_period")}
              className="rounded border px-2 py-1 text-sm min-w-[180px]"
              maxLength={200} />
          )}
          <Button size="sm" variant="outline"
            data-testid="guided-warranty-override-add"
            disabled={!newTrade || (newPeriod === "custom" && !newCustom.trim())}
            onClick={() => {
              onAdd(newTrade, newPeriod, newPeriod === "custom" ? newCustom : null);
              setNewTrade(""); setNewPeriod("1_year"); setNewCustom("");
            }}>{t("guided.add_trade_override")}</Button>
        </div>
      )}
      {available.length === 0 && (overrides || []).length === (activeTrades || []).length && (activeTrades || []).length > 0 && (
        <div className="text-xs text-muted-foreground"
             data-testid="guided-warranty-override-no-more">
          Every active trade already has an override.
        </div>
      )}
      {(activeTrades || []).length === 0 && (
        <div className="text-xs text-amber-800"
             data-testid="guided-warranty-override-no-trades">
          Select active trades in Business &amp; Trades to add per-trade overrides.
        </div>
      )}
    </div>
  );
};

const WarrantyGuidedPanel = ({
  family, userId, companyProfile, guidedRecord,
  onSaveGuided, onSetLater, onHandleOutside, onSwitchToEditor,
  onNavigateToBusinessAndTrades,
}) => {
  void family;
  const { t, lang } = useLang();
  const questions = getFamilyQuestions("warranty", lang);
  const allowlist = WARRANTY_ALLOWLIST;
  const baseline = guidedBaselineFromRecord(guidedRecord);
  const savedMode = (() => {
    const ws = guidedRecord?.wording_source || null;
    if (ws === "generated" || ws === "customized") return "recommended";
    if (ws === "outside_jobclose_ai") return "outside";
    if (ws === "legacy") return "existing";
    return "unselected";
  })();
  const [mode, setMode] = useState(savedMode);
  const modeSelected = mode !== "unselected";
  const [answers, setAnswers] = useState(() => {
    const p = readGuidedDraft(userId, "warranty");
    if (p && p.answers) return p.answers;
    if (savedMode === "recommended")
      return { ...emptyAnswersForFamily("warranty"), ...baseline.answers };
    return { ...baseline.answers };
  });
  const [adjustments, setAdjustments] = useState(() => {
    const p = readGuidedDraft(userId, "warranty");
    if (p && p.adjustments) return p.adjustments;
    return { ...(baseline.adjustments || {}) };
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const activeTrades = Array.isArray(companyProfile?.trade_categories)
    ? companyProfile.trade_categories : [];
  const gen = useMemo(() => {
    const base = generateWarrantyWording({
      companyName: companyProfile?.company_name || "",
      phone: companyProfile?.phone || "",
      email: companyProfile?.email || "",
      answers, activeTrades,
      lang,
    });
    if (!base.ok || !base.wording) return base;
    return { ...base, wording: applyHelpMeChangeItWarranty(base.wording, adjustments) };
  }, [answers, adjustments, companyProfile, activeTrades, lang]);
  useEffect(() => {
    if (!userId) return;
    if (!modeSelected) { clearGuidedDraft(userId, "warranty"); return; }
    const sa = (guidedRecord && guidedRecord.answers) || {};
    const sadj = (guidedRecord && guidedRecord.adjustments) || {};
    let adjDiff = false;
    try { adjDiff = JSON.stringify(adjustments || {}) !== JSON.stringify(sadj || {}); }
    catch (_e) { adjDiff = Object.keys(adjustments || {}).length > 0; }
    const dirty = answersDifferMeaningfully(answers, sa, "warranty") || adjDiff;
    if (dirty) writeGuidedDraft(userId, "warranty", { answers, adjustments });
    else clearGuidedDraft(userId, "warranty");
  }, [userId, answers, adjustments, guidedRecord, modeSelected]);
  const sa = (guidedRecord && guidedRecord.answers) || {};
  const dirty = modeSelected
    ? (isGuidedDirty({ answers, adjustments },
        { answers: sa, adjustments: baseline.adjustments || {},
          updated_at: baseline.updated_at, wording_source: baseline.wording_source },
        "warranty") || mode !== savedMode)
    : false;
  const missingCompany = !companyProfile?.company_name;
  const wo = answers.workmanship_offered || "yes";
  const cov = Array.isArray(answers.workmanship_coverage) ? answers.workmanship_coverage : [];
  const exc = Array.isArray(answers.workmanship_exclusions) ? answers.workmanship_exclusions : [];
  const wp = answers.workmanship_period;
  const cwpMissing = wp === "custom" &&
    !(answers.custom_workmanship_period || "").trim();
  const sscMissing = answers.service_contact_rule === "specific" &&
    !(answers.specific_service_contact || "").trim();
  const overrideCustomMissing = (answers.trade_overrides || []).some(
    (o) => o.period === "custom" && !(o.custom_period || "").trim(),
  );
  const canUseRecommended = !missingCompany && !busy && !!gen.ok &&
    !cwpMissing && !sscMissing && !overrideCustomMissing &&
    (wo !== "yes" || (cov.length >= 1 && exc.length >= 1));
  const doUseRecommended = async () => {
    if (!canUseRecommended) return;
    setError(""); setBusy(true);
    try {
      // Never issues, activates, sends, shares, publishes, signs, approves,
      // or denies a warranty. Saves defaults + wording only.
      await onSaveGuided({
        answers, action: "use_recommended", wording_source: "generated",
        resolved_fields: gen.wording, adjustments,
      });
      setMode("recommended");
      clearGuidedDraft(userId, "warranty");
    } catch (e) { setError(String((e && e.message) || e)); }
    finally { setBusy(false); }
  };
  const doDiscard = () => {
    setMode(savedMode);
    setAnswers({ ...baseline.answers });
    setAdjustments({ ...(baseline.adjustments || {}) });
    clearGuidedDraft(userId, "warranty"); setError("");
  };
  if (!modeSelected) {
    return (
      <div className="space-y-3" data-testid="guided-warranty-first-choice">
        <div className="text-sm font-semibold">How do you want to handle workmanship warranties?</div>
        <div className="grid gap-2">
          <Button variant="default" data-testid="guided-warranty-first-use-recommended"
            onClick={() => {
              setMode("recommended");
              setAnswers({ ...emptyAnswersForFamily("warranty"), ...baseline.answers });
            }}>{t("guided.use_recommended_warranty")}</Button>
          <Button variant="outline" data-testid="guided-warranty-first-use-existing"
            onClick={() => onSwitchToEditor({ prefill: null, source: "use_existing" })}>
            Use my existing warranty wording</Button>
          <Button variant="outline" data-testid="guided-warranty-first-handle-outside"
            onClick={() => onHandleOutside()}>{t("guided.handle_outside_warranty")}</Button>
          <Button variant="outline" size="sm" data-testid="guided-warranty-first-set-later"
            onClick={() => onSetLater()}>{t("guided.set_later")}</Button>
        </div>
      </div>
    );
  }
  const questionVisible = (q) => {
    if (!q.onlyWhen) return true;
    return answers[q.onlyWhen.key] === q.onlyWhen.equals;
  };
  return (
    <div className="space-y-4" data-testid="guided-warranty-panel">
      {missingCompany && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">{t("guided.company_incomplete")}</div>
          <div>Please finish your Business &amp; Trades setup before generating Warranty wording.</div>
          <Button size="sm" variant="outline" className="mt-2"
            onClick={onNavigateToBusinessAndTrades}
            data-testid="guided-warranty-goto-business">Open Business &amp; Trades</Button>
        </div>
      )}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
           data-testid="guided-warranty-safety-note">
        JobClose AI prepares warranty defaults for future documents only.
        Nothing is automatically issued, activated, sent, shared, published, or signed.
        Existing warranty documents remain unchanged historical snapshots.
        Calendar dates alone are not treated as completion confirmation.
      </div>
      <div className="space-y-3" data-testid="guided-warranty-questions">
        {questions.map((q) => {
          if (!questionVisible(q)) return null;
          if (q.kind === "chip") {
            const cur = answers[q.key] ?? q.defaultValue;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-warranty-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (<div className="text-xs text-muted-foreground"
                  data-testid={`guided-warranty-help-${q.id}`}>{q.helpText}</div>)}
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((c) => (
                    <Button key={c.value} size="sm"
                      variant={cur === c.value ? "default" : "outline"}
                      data-testid={`guided-warranty-${q.id}-${c.value}`}
                      onClick={() => setAnswers((a) => setAnswer(a, q.key, c.value))}>
                      {c.label}</Button>
                  ))}
                </div>
                {(q.conditionalInputs || []).filter((ci) => ci.when === cur).map((ci) => (
                  <div key={ci.key} className="pt-1">
                    <input type="text"
                      value={answers[ci.key] || ""}
                      placeholder={ci.placeholder || ""}
                      maxLength={ci.max || 500}
                      data-testid={`guided-warranty-${q.id}-${ci.key}-input`}
                      onChange={(e) => setAnswers((a) => setAnswer(a, ci.key, e.target.value))}
                      className="w-full rounded border px-2 py-1 text-sm" />
                    <div className="text-xs text-muted-foreground">{ci.label}</div>
                  </div>
                ))}
              </div>
            );
          }
          if (q.kind === "checkbox_multiselect") {
            const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
            const belowMin = q.minSelected && cur.length < q.minSelected;
            return (
              <div key={q.id} className="space-y-2" data-testid={`guided-warranty-q-${q.id}`}>
                <div className="text-sm font-medium">{q.prompt}</div>
                {q.helpText && (<div className="text-xs text-muted-foreground"
                  data-testid={`guided-warranty-help-${q.id}`}>{q.helpText}</div>)}
                <div className="flex flex-col gap-1">
                  {q.choices.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={cur.includes(c.value)}
                        data-testid={`guided-warranty-${q.id}-${c.value}`}
                        onCheckedChange={() => setAnswers((a) =>
                          toggleMultiSelect(a, q.key, c.value, {
                            mutuallyExclusive: q.mutuallyExclusive || null,
                          }))} />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
                {belowMin && (<div className="text-xs text-amber-800"
                  data-testid={`guided-warranty-${q.id}-min-warning`}>Select at least one option before saving.</div>)}
              </div>
            );
          }
          return null;
        })}
      </div>
      <div className="space-y-2" data-testid="guided-warranty-trade-toggle">
        <div className="text-sm font-medium">Do any of your trades use a different workmanship warranty period?</div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm"
            variant={!answers.use_trade_overrides ? "default" : "outline"}
            data-testid="guided-warranty-use-overrides-no"
            onClick={() => setAnswers((a) => setAnswer(a, "use_trade_overrides", false))}>
            No — use my standard warranty for all trades</Button>
          <Button size="sm"
            variant={answers.use_trade_overrides ? "default" : "outline"}
            data-testid="guided-warranty-use-overrides-yes"
            onClick={() => setAnswers((a) => setAnswer(a, "use_trade_overrides", true))}>
            Yes — add simple trade overrides</Button>
        </div>
        {answers.use_trade_overrides === true && (
          <TradeOverridesSection
            activeTrades={activeTrades}
            overrides={answers.trade_overrides || []}
            onAdd={(t, p, c) => setAnswers((a) => addTradeOverride(a, t, p, c))}
            onUpdate={(t, p, c) => setAnswers((a) => updateTradeOverride(a, t, p, c))}
            onRemove={(t) => setAnswers((a) => removeTradeOverride(a, t))} />
        )}
      </div>
      {gen.ok && (
        <div className="rounded-md border p-3 space-y-2" data-testid="guided-warranty-preview">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("guided.recommended_warranty_wording")}</div>
          {allowlist.map((k) => {
            const v = gen.wording[k];
            if (!v) return null;
            return (
              <div key={k} className="text-sm whitespace-pre-wrap"
                   data-testid={`guided-warranty-preview-${k}`}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {k.replace(/_/g, " ")}</div>
                {v}
              </div>
            );
          })}
        </div>
      )}
      {!helpOpen && (
        <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}
          aria-expanded={false} data-testid="guided-warranty-help-open">
          Help me change it
          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      )}
      {helpOpen && (
        <WarrantyHelpPanel
          tone={answers.tone || "professional"}
          onToneChange={(t) => setAnswers((a) => setAnswer(a, "tone", t))}
          length={adjustments.length}
          onLengthChange={(l) => setAdjustments((p) => ({ ...p, length: l }))}
          emphasize={adjustments.emphasize || []}
          onToggleEmphasize={(v) => setAdjustments((p) => {
            const cur = new Set(p.emphasize || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            return { ...p, emphasize: Array.from(cur) };
          })}
          onClose={() => setHelpOpen(false)} />
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={doUseRecommended} disabled={!canUseRecommended}
          data-testid="guided-warranty-use-recommended">
          Use JobClose AI recommended wording</Button>
        <Button variant="outline"
          onClick={() => onSwitchToEditor({ prefill: gen.wording, wording_source: "customized" })}
          disabled={!gen.ok} data-testid="guided-warranty-edit-wording">{t("guided.edit_wording")}</Button>
        <Button variant="outline" onClick={() => onHandleOutside()}
          data-testid="guided-warranty-handle-outside">{t("guided.handle_outside_warranty")}</Button>
        <Button variant="outline" size="sm" onClick={() => onSetLater()}
          data-testid="guided-warranty-set-later">{t("guided.set_later")}</Button>
      </div>
      {dirty && (
        <div className="flex items-center gap-3 text-xs" data-testid="guided-warranty-dirty-row">
          <Badge variant="secondary">{t("guided.unsaved_changes")}</Badge>
          <Button size="sm" variant="ghost" onClick={doDiscard}
            data-testid="guided-warranty-discard-draft">{t("guided.discard_draft")}</Button>
        </div>
      )}
      {error && (<div className="text-xs text-red-600"
        data-testid="guided-warranty-error">{error}</div>)}
    </div>
  );
};



const GuidedDocumentFamilyPanel = (props) => {
  if (props.family === "proposal") return <ProposalGuidedPanel {...props} />;
  if (props.family === "contract") return <ContractGuidedPanel {...props} />;
  if (props.family === "invoice")  return <InvoiceGuidedPanel {...props} />;
  if (props.family === "customer_update") return <CustomerUpdateGuidedPanel {...props} />;
  if (props.family === "closeout") return <CloseoutGuidedPanel {...props} />;
  if (props.family === "warranty") return <WarrantyGuidedPanel {...props} />;
  return null;
};

export default GuidedDocumentFamilyPanel;
